import { makeAutoObservable, set, toJS } from 'mobx';
import { message } from 'antd';
import { CanvasUtils } from '../utils/CanvasUtils';
import { PageTypes, ButtonMode } from '../config';
import { MAX_CONCURRENT_TASKS } from '../constant';
import { Utils } from '../utils/Utils';

function updateQueryParam(key, value) {
    const url = new URL(window.location.href);
    if (value === null || value === undefined) {
        url.searchParams.delete(key);
    } else {
        url.searchParams.set(key, value);
    }
    window.history.pushState({}, '', url);
}

export class ViewManager {
    _libState = null;
    _currentPage = 'homePage';
    _selectedMenu = 1;
    _formData = null;
    _selectedPages = [];
    _currentPageToDisplay = 4;
    _modelOpen = false;
    _imageData = null;
    _activePageNo = null;
    _snappingPoints = null;
    _isLoading = false;
    _projectId = null;
    _projects = [];
    _projectsError = null;
    _hasProjectsError = false;
    _uiPages = [];
    _pageStatus = [];
    _possibleClosets = [];
    _selectedPagePossibleClosets = [];
    _unitForm = false;
    _unitData = null;
    _units = null;
    _unitQuantity = null;
    _hasUserFinalized = false;
    _openForm = false;
    _pdfFile = null;
    _matrixCalculation = false;
    _buttonMode = ButtonMode.Unit;
    _closetForm = false;
    _isDropDownOpen = false;
    _fullPdf = false;
    _isLoggedIn = false;
    _userData = null;
    _isLoader = false;
    _usersList = [];
    _superAdmin = false;
    _pageOwners = [];
    constructor(libState) {
        makeAutoObservable(this);
        this._libState = libState;
    }

    get currentPage() {
        return this._currentPage;
    }

    get isLoading() {
        return this._isLoading;
    }

    get activePageNo() {
        return this._activePageNo;
    }

    get selectedMenu() {
        return this._selectedMenu;
    }

    get formData() {
        return this._formData;
    }

    get selectedPages() {
        return this._selectedPages;
    }

    get snappingPoints() {
        return this._snappingPoints;
    }

    get currentPageToDisplay() {
        return this._currentPageToDisplay;
    }
    get modelOpen() {
        return this._modelOpen;
    }

    get imageData() {
        return this._imageData;
    }

    get pdfCanvas() {
        return this._pdfCanvas;
    }

    get pdfDoc() {
        return this._pdfDoc;
    }

    get projects() {
        return this._projects;
    }

    get projectsError() {
        return this._projectsError;
    }

    get projectId() {
        return this._projectId;
    }

    get uiPages() {
        return this._uiPages;
    }

    get pageStatus() {
        return this._pageStatus;
    }

    get possibleClosets() {
        return this._possibleClosets;
    }

    get selectedPagePossibleClosets() {
        return this._selectedPagePossibleClosets;
    }
    get unitData() {
        return this._unitData;
    }

    get units() {
        return this._units;
    }

    get unitQuantity() {
        return this._unitQuantity;
    }

    get hasUserFinalized() {
        return this._hasUserFinalized;
    }

    get openForm() {
        return this._openForm;
    }

    get pdfFile() {
        return this._pdfFile;
    }

    get matrixCalculation() {
        return this._matrixCalculation;
    }

    get buttonMode() {
        return this._buttonMode;
    }

    get isDropDownOpen() {
        return this._isDropDownOpen;
    }

    get fullPdf() {
        return this._fullPdf;
    }

    get hasProjectsError() {
        return this._hasProjectsError;
    }

    get isLoggedIn() {
        return this._isLoggedIn;
    }

    get userData() {
        return this._userData;
    }

    get isLoader() {
        return this._isLoader;
    }

    get usersList() {
        return this._usersList;
    }

    get superAdmin() {
        return this._superAdmin;
    }

    get pageOwners() {
        return this._pageOwners;
    }

    setPageOwners(pageOwners) {
        this._pageOwners = pageOwners;
    }

    setSuperAdmin(superAdmin) {
        this._superAdmin = superAdmin;
    }

    setUsersList(usersList) {
        this._usersList = usersList;
    }

    setFullPdf(fullPdf) {
        this._fullPdf = fullPdf;
    }

    setIsDropDownOpen(isDropDownOpen) {
        this._isDropDownOpen = isDropDownOpen;
    }

    setButtonMode(buttonMode) {
        this._buttonMode = buttonMode;
    }

    setMatrixCalculation(matrixCalculation) {
        this._matrixCalculation = matrixCalculation;
    }

    setPdfFile(pdfFile) {
        this._pdfFile = pdfFile;
    }

    setOpenForm(openForm) {
        this._openForm = openForm;
    }

    setHasUserFinalized(hasUserFinalized) {
        this._hasUserFinalized = hasUserFinalized;
    }

    setUnitData(unitData) {
        this._unitData = unitData;
    }

    setUnits(units) {
        this._units = units;
    }

    setUnitQuantity(unitQuantity) {
        this._unitQuantity = unitQuantity;
    }

    setSelectedPagePossibleClosets(selectedPagePossibleClosets) {
        this._selectedPagePossibleClosets = selectedPagePossibleClosets;
    }

    setPossibleClosets(possibleClosets) {
        this._possibleClosets = possibleClosets;
    }
    get unitForm() {
        return this._unitForm;
    }

    setUnitForm(unitForm) {
        this._unitForm = unitForm;
    }

    get closetForm() {
        return this._closetForm;
    }

    setClosetForm(closetForm) {
        this._closetForm = closetForm;
    }

    setPageStatus(pageStatus) {
        this._pageStatus.push(...pageStatus);
        this._pageStatus = [...this.pageStatus];
    }

    updatePageStatus(pageNo, status) {
        const pageIndex = this.pageStatus.findIndex(
            (page) => page.pageNo === pageNo,
        );

        if (pageIndex !== -1) {
            this.pageStatus[pageIndex].status = status;
        }
        this._pageStatus = [...this.pageStatus];
    }

    addUiPages(page) {
        this._uiPages.push(page);
    }

    setUiPages(uiPages) {
        this._uiPages = uiPages;
    }

    setProjectId(projectId) {
        this._projectId = projectId;
    }

    setPdfCanvas(pdfCanvas) {
        this._pdfCanvas = pdfCanvas;
    }
    setCurrentPage(currentPage) {
        this._currentPage = currentPage;
    }

    setSnappingPoints(snappingPoints) {
        this._snappingPoints = snappingPoints;
    }

    setSelectedMenu(selectedMenu) {
        this._selectedMenu = selectedMenu;
    }

    setIsLoading(isLoading) {
        this._isLoading = isLoading;
    }

    setFormData(formData) {
        this._formData = formData;
    }

    setSelectedPages(selectedPages) {
        this._selectedPages = selectedPages;
        if (selectedPages.length === 0) {
            return;
        }
        this._libState.closetManager.setSelectedPageNo(
            selectedPages[0].pageNum,
        );
    }

    setCurrentPageToDisplay(currentPageToDisplay) {
        this._currentPageToDisplay = currentPageToDisplay;
    }

    setModelOpen(modelOpen) {
        this._modelOpen = modelOpen;
    }

    setImageData(imageData) {
        this._imageData = imageData;
    }

    setPdfDoc(pdfDoc) {
        this._pdfDoc = pdfDoc;
    }

    setActivePageNo(activePageNo) {
        this._activePageNo = activePageNo;
    }

    async bringChangesToClosetAccordingToSelectedPages() {
        await this._libState.closetManager.setSelectedPageNo(this.activePageNo);
        if (this._libState.drawingManager.isDrawingModeOn) {
            this._libState.drawingManager.stopDrawing();
        }
    }
    async fetchProjects() {
        this._isLoading = true;
        this._projectsError = null;
        this._hasProjectsError = false;

        try {
            const renameData =
                await this._libState.apiManager.fetchAllProjects();
            if (!renameData) {
                message.error(renameData.message);
                this._hasProjectsError = true;
            }
            this._projects = renameData.data || [];
        } catch (error) {
            console.error('Error fetching projects:', error);
            message.error('Error fetching projects. Please try again.');
            this._projectsError = error.message;
            this._hasProjectsError = true;
        } finally {
            this._isLoading = false;
        }
    }

    async uploadPdf() {
        let bool = false;
        try {
            this._isLoading = true;

            bool = await this._libState.apiManager.clientSideCreateAPI();

            const responseData =
                await this._libState.apiManager.createProject();
            if (!responseData) {
                message.error(responseData.message);
                return;
            }
            const projectId = responseData.data.project.id;
            this.setProjectId(projectId);
            updateQueryParam('project_id', projectId);
            const current = toJS(this.pageOwners) || [];
            const allPages = this.selectedPages || [];
            let pageToUser = [];
            current.forEach((entry) => {
                entry.pages.forEach((p) => {
                    pageToUser[+p] = entry.user_id;
                });
            });
            const pagesToAssign = allPages.map((p) => +p.pageNum);
            pagesToAssign.forEach((pageNum) => {
                pageToUser[pageNum] = this.userData.id;
            });
            let ownersMap = [];
            allPages.forEach((page) => {
                const pageNum = +page.pageNum;
                const user_id = pageToUser[pageNum];
                if (user_id) {
                    if (!ownersMap[user_id]) ownersMap[user_id] = [];
                    ownersMap[user_id].push(pageNum);
                } else {
                    if (!ownersMap['unassigned']) ownersMap['unassigned'] = [];
                    ownersMap['unassigned'].push(pageNum);
                }
            });
            const filtered = Object.entries(ownersMap)
                .map(([user_id, pages]) => ({
                    user_id,
                    pages,
                }))
                .filter((entry) => entry.pages.length > 0);
            this.setPageOwners(filtered);
            await this._libState.apiManager.assignPageAccess();
            this.setSuperAdmin(true);
            this._formData.pdfFile.preview = responseData.data.pdf_file;
            const bin_file_url =
                responseData.data.bin_files[`${this.selectedPages[0].pageNum}`];
            const snappingPoints = await Utils.getArrayFromBinFile(
                bin_file_url,
                this.selectedPages[0].pageNum,
            );
            this.setSnappingPoints(snappingPoints);
            // Iterate over json_files keys
            Object.keys(responseData.data.bin_files).forEach((key) => {
                const pageNum = key.split('_')[0]; // Extract page number from "2_page", "3_page", etc.

                // Find index of pageNum in statusList and update the status if found
                this.pageStatus.push({ pageNo: pageNum, status: false });
                this.updatePageStatus(pageNum, true);
            });
            this.fetchAllPageStatus();
            message.success('Project created successfully.');
        } catch (error) {
            console.error('Error uploading PDF:', error);
            message.error('Error uploading PDF. Please try again.');
        } finally {
            this._isLoading = !bool;
        }
    }

    async renderPage() {
        if (!this.pdfCanvas) return;
        this.pdfCanvas
            .getContext('2d')
            .clearRect(0, 0, this.pdfCanvas.width, this.pdfCanvas.height);

        const pageNo = this.imageData.pageNum;
        const page = await this.pdfDoc.getPage(pageNo);
        const viewport = page.getViewport({ scale: 1 });

        this.pdfCanvas.width = viewport.width;
        this.pdfCanvas.height = viewport.height;
        const renderContext = {
            canvasContext: this.pdfCanvas.getContext('2d'),
            viewport,
        };
        await page.render(renderContext).promise;
    }

    async openProject() {
        this.setIsLoading(true);
        if (!this.projectId) return;

        try {
            let updatePdf = false;
            const resData = await this._libState.apiManager.accessData();
            this.processPageData(resData);
            const responseData = await this._libState.apiManager.openProject();
            if (!responseData) {
                message.error(responseData.message);
            }
            if (responseData?.data) {
                if (
                    this.superAdmin ||
                    this.userData.id == responseData.data.user_id
                ) {
                    this.setSuperAdmin(true);
                }
                this.setProjectId(responseData.data.id);
                if (
                    responseData.data.trimmed_pdf &&
                    responseData.data.pdf_file
                ) {
                    const trimmedPdfResponse = await fetch(
                        responseData.data.trimmed_pdf,
                    );
                    const pdfResponse = await fetch(responseData.data.pdf_file);

                    if (pdfResponse.ok && trimmedPdfResponse.ok) {
                        const pdfBlobPromise = pdfResponse.blob();
                        const trimmedPdfBlob = await trimmedPdfResponse.blob();
                        this._libState.closetManager.setPdfBuffer(
                            trimmedPdfBlob,
                        );
                        if (!trimmedPdfResponse.ok) {
                            this._libState.closetManager.setPdfBuffer(
                                await pdfBlobPromise,
                            );
                        } else {
                            pdfBlobPromise
                                .then((pdfBlob) => {
                                    this.setPdfFile(pdfBlob);
                                    this._libState.matrixManager.setPdfBuffer(
                                        pdfBlob,
                                    );
                                    this.setPdfFile(pdfBlob);
                                    this.setFullPdf(true);
                                })
                                .catch((error) => {
                                    console.error(
                                        'Failed to process pdfBlob:',
                                        error,
                                    );
                                });
                        }
                    } else if (pdfResponse.ok && !trimmedPdfResponse.ok) {
                        const pdfBlob = await pdfResponse.blob();
                        this._libState.closetManager.setPdfBuffer(pdfBlob);
                        this._libState.matrixManager.setPdfBuffer(pdfBlob);
                        this.setPdfFile(pdfBlob);
                        this.setFullPdf(true);
                        updatePdf = true;
                    } else {
                        console.error('Failed to fetch PDF file');
                    }
                }
                let allPages = [];
                responseData.data.pages.map((page) => {
                    let obj = {};
                    obj.name = 'Page ' + page;
                    obj.pageNum = page;
                    allPages.push(obj);
                });
                this.setSelectedPages(allPages);
                this._libState.closetManager.setSelectedPageNo(
                    allPages[0].pageNum,
                );
                const bin_url =
                    responseData.data.bin_files[
                        `${this.selectedPages[0].pageNum}_page`
                    ];

                this.createPageStatus();
                const snappingPointsData = await Utils.getArrayFromBinFile(
                    bin_url,
                    this.selectedPages[0].pageNum,
                );
                this.setSnappingPoints(snappingPointsData);
                let dataObj = {};
                dataObj.projectName = responseData.data.name;
                dataObj.projectCreator = responseData.data.creator;
                dataObj.projectDescription = responseData.data.description;
                let pdf_file = {};
                pdf_file['preview'] = responseData.data.pdf_file;
                dataObj.pdfFile = pdf_file;
                this.setCurrentPage(PageTypes.WorkSpace);
                this.setSelectedMenu(2);
                this.setIsLoading(false);
                const closetFile = await fetch(responseData.data.closets_file);
                if (
                    typeof closetFile.url === 'string' &&
                    closetFile.url.trim() !== '' &&
                    closetFile.url.includes('undefined')
                )
                    this._libState.closetManager.setCanCalibrate(true);
                const closetData = JSON.parse(await closetFile.text());
                await this._libState.closetManager.renderPage();
                this._libState.closetManager.fitToScreen();
                if (closetData.scaleInfo && closetData.scaleInfo.length > 0) {
                    this._libState.closetManager.setScalesOfPages(
                        closetData.scaleInfo,
                    );
                }
                dataObj.projectScope = closetData.projectScope;
                dataObj.customScope = closetData.customScope;
                dataObj.color = closetData.color;
                dataObj.handleStyle = closetData.handleStyle;
                dataObj.handleColor = closetData.handleColor;
                dataObj.rodColor = closetData.rodColor;
                dataObj.doorColor = closetData.doorColor;
                dataObj.drawerColor = closetData.drawerColor;
                dataObj.drawerStyle = closetData.drawerStyle;
                dataObj.doorStyle = closetData.doorStyle;
                dataObj.legs = closetData.legs;
                dataObj.shelf = closetData.shelf;
                dataObj.topDoors = closetData.topDoors;
                dataObj.bottomDoors = closetData.bottomDoors;
                this.setFormData(dataObj);
                this._libState.apiManager.setProjectId(
                    closetData.clientProjectId,
                );
                this._libState.apiManager.setParentProjectId(
                    closetData.clientParentProjectId,
                );
                this._libState.closetManager.drawExistingUnits(
                    closetData.units,
                );
                this._libState.closetManager.drawExistingClosets(
                    closetData.closets,
                );
                this._libState.matrixManager.setMatrixData(
                    closetData.matrixData,
                );
                if (this._libState.closetManager.units.length == 0)
                    this._libState.closetManager.setCanCalibrate(true);
                this._libState.closetManager.drawAllClosets();
                await this.showPossibleClosets();
                if (updatePdf) {
                    const response =
                        await this._libState.apiManager.updatePages();
                    if (!response) {
                        message.error(response.message);
                    }
                }
            }
        } catch (error) {
            this.setIsLoading(false);
            console.error('Failed to open project:', error);
            message.error('Failed to open project. Please try again.');
        }
    }

    async updatePages() {
        this.setIsLoading(true);

        try {
            const responseData = await this._libState.apiManager.updatePages();
            if (!responseData) {
                message.error(responseData.message);
            }
            const current = toJS(this.pageOwners) || [];
            const allPages = this.selectedPages || [];
            let pageToUser = [];
            current.forEach((entry) => {
                entry.pages.forEach((p) => {
                    pageToUser[+p] = entry.user_id;
                });
            });
            const pagesToAssign = allPages
                .map((p) => +p.pageNum)
                .filter((pageNum) => !pageToUser[pageNum]); // Keep only unassigned pages

            pagesToAssign.forEach((pageNum) => {
                pageToUser[pageNum] = this.userData.id; // Assign only unassigned pages
            });
            let ownersMap = [];
            allPages.forEach((page) => {
                const pageNum = +page.pageNum;
                const user_id = pageToUser[pageNum];
                if (user_id) {
                    if (!ownersMap[user_id]) ownersMap[user_id] = [];
                    ownersMap[user_id].push(pageNum);
                } else {
                    if (!ownersMap['unassigned']) ownersMap['unassigned'] = [];
                    ownersMap['unassigned'].push(pageNum);
                }
            });
            const filtered = Object.entries(ownersMap)
                .map(([user_id, pages]) => ({
                    user_id,
                    pages,
                }))
                .filter((entry) => entry.pages.length > 0);
            this.setPageOwners(filtered);
            await this._libState.apiManager.assignPageAccess();
            this.setIsLoading(false);
        } catch (error) {
            this.setIsLoading(false);
            console.error('Error updating pages:', error);
        }
    }

    createPageStatus() {
        let pageArr = [];
        this.selectedPages.forEach((page) => {
            if (
                this.pageStatus.find((status) => status.pageNo == page.pageNum)
            ) {
                return;
            }
            pageArr.push({ pageNo: page.pageNum, status: false });
        });
        this.setPageStatus(pageArr);
    }

    handlePageNameChange = (pageNum, newName) => {
        this.uiPages.map((page) => {
            if (page.pageNum === pageNum) {
                page.name = newName;
            }
        });
    };

    fetchAllPageStatus() {
        let allPagesStatusTrue = true;
        if (this.pageStatus.length === 0) return false;
        this.pageStatus.map((page) => {
            if (!page.status) {
                allPagesStatusTrue = false;
            }
        });
        return allPagesStatusTrue;
    }

    setImageUrlInUiPages = (pageNum, imageUrl) => {
        this.uiPages.map((page) => {
            if (page.pageNum === pageNum && page.tempUrl) {
                page.imageUrl = imageUrl;
                page.tempUrl = false;
            }
        });
    };

    isTempUrlInUiPages = (pageNum) => {
        let isTempUrl = false;
        this.uiPages.map((page) => {
            if (page.pageNum === pageNum && page.tempUrl) {
                isTempUrl = true;
            }
        });
        return isTempUrl;
    };

    renderAllPages = async (pdfDoc) => {
        if (!pdfDoc || !pdfDoc.numPages) {
            message.error('Invalid PDF document or no pages to render.');
            console.warn('Invalid PDF document or no pages to render.');
            return;
        }
        const pageNumbers = Array.from(
            { length: pdfDoc.numPages },
            (_, index) => index + 1,
        );

        const executeBatch = async (batch) => {
            await Promise.allSettled(
                batch.map((pageNum) => this.renderPageForUi(pdfDoc, pageNum)),
            );
        };

        for (let i = 0; i < pageNumbers.length; i += MAX_CONCURRENT_TASKS) {
            const batch = pageNumbers.slice(i, i + MAX_CONCURRENT_TASKS);
            await executeBatch(batch);
        }
    };
    renderPageForUi = async (pdfDoc, pageNum) => {
        if (this.hasUserFinalized || !this.isTempUrlInUiPages(pageNum)) return;

        const { imageUrl, pageNumber } = await CanvasUtils.renderPageCanvas(
            pdfDoc,
            pageNum,
        );
        this.setImageUrlInUiPages(pageNumber, imageUrl);
    };

    async showPossibleClosets() {
        const selectedPage = this._libState.closetManager.selectedPageNo;
        const responseData =
            await this._libState.apiManager.showPossibleClosets();
        if (!responseData) {
            message.error(responseData.message);
        }
        this.setPossibleClosets(responseData.data.combined_closets);
        this.setSelectedPagePossibleClosets(this.possibleClosets[selectedPage]);
    }

    updatePossibleClosets() {
        if (this.possibleClosets) {
            const selectedPage = this._libState.closetManager.selectedPageNo;
            this.setSelectedPagePossibleClosets(
                this.possibleClosets[selectedPage],
            );
            return true;
        } else {
            return false;
        }
    }

    setHasProjectsError(hasError) {
        this._hasProjectsError = hasError;
    }
    setIsLoggedIn(isLoggedIn) {
        this._isLoggedIn = isLoggedIn;
    }
    setUserData(userData) {
        this._userData = userData;
    }
    setIsLoader(isLoader) {
        this._isLoader = isLoader;
    }
    isSuperAdmin() {
        const user = this.usersList.find((u) => u.id === this.userData.id);
        if (user && user.is_superadmin) {
            this.setSuperAdmin(true);
        } else {
            return;
        }
    }

    processPageData(responseData) {
        if (!responseData || !responseData.data) {
            console.warn('Failed to process page data.');
            message.error('Failed to process page data.');
            return;
        }
        const userPageAccessList = this.usersList
            .map((user) => {
                const pages = responseData.data.page_access
                    .filter((access) => access.user_id === user.id)
                    .map((access) => access.page_number);
                return {
                    user_id: user.id,
                    pages,
                };
            })
            .filter((user) => user.pages.length > 0);
        this.setPageOwners(userPageAccessList);
    }

    doUserHasAccess(
        selectedPage = this._libState.closetManager.selectedPageNo,
    ) {
        if (!this.userData || !this.pageOwners || selectedPage == null) {
            console.warn('Missing userData, pageOwners, or selectedPageNo');
            return false;
        }
        const userId = String(this.userData.id);
        const pageStr = String(selectedPage);

        const ownerEntry = this.pageOwners.find(
            (owner) => String(owner.user_id) === userId,
        );
        if (!ownerEntry) {
            return false;
        }

        const hasAccess = ownerEntry.pages.some((p) => String(p) === pageStr);
        return hasAccess;
    }

    swapPageAccess() {
        const userId = String(this.userData.id);
        const selectedPageNo = String(
            this._libState.closetManager.selectedPageNo,
        );
        let userFound = false;
        const newPageOwners = this.pageOwners.map((owner) => {
            let updatedPages = owner.pages.filter(
                (p) => String(p) !== selectedPageNo,
            );
            if (String(owner.user_id) === userId) {
                userFound = true;
                if (!updatedPages.includes(selectedPageNo)) {
                    updatedPages = [...updatedPages, selectedPageNo];
                }
            }
            return {
                ...owner,
                pages: updatedPages,
            };
        });
        if (!userFound) {
            newPageOwners.push({
                user_id: this.userData.id,
                pages: [selectedPageNo],
            });
        }
        this.setPageOwners(newPageOwners);
    }
}
